import styled from '@/styles';
import { WorkerTabs } from '@/util/axios/types';

// Job-page-only fork of the tab bar (F13). 2026-07-30 prototype alignment
// (Rod re-opened delta C): the interim Browse `<select>` sub-filter is gone —
// filtering is now the prototype's "Filter by" popover (FilterByPopover,
// rendered by the modal's toolbar, available on every tab), and the old
// four-tab layout's scopes reduce to three top-level tabs: "<Skill> pool",
// Favorites, "Browse all workers" (ui-prototype/preferred-worker-pool
// #tabBtnPool/#tabBtnFavorites/#tabBtnBrowse). The prototype carries no
// Highly Rated / Others surface, so those scopes are gone with the select.
// See src/routes/.../StaffingStep/Common/StaffingTabs.tsx for the surface
// this is forked from — that file stays byte-for-byte unchanged (see F13
// brief hard constraint).

export type StaffingTabV2Value = 'preferred_pool' | 'favourites' | 'browse';

// Resolves a top-level tab into the `WorkerTabs` value the list fetch (F14)
// needs. Browse always fetches All — GravyTrained/Past-workers narrowing now
// travels as the composable gravyTrained/pastWorkers params (delta C), not
// as a tab value.
export const resolveWorkerTab = (tab: StaffingTabV2Value): WorkerTabs => {
  if (tab === 'preferred_pool') {
    return WorkerTabs.PreferredPool;
  }
  if (tab === 'favourites') {
    return WorkerTabs.Favorites;
  }
  return WorkerTabs.All;
};

const topTabs = (
  skillName?: string,
): { value: StaffingTabV2Value; label: string }[] => [
  {
    value: 'preferred_pool',
    label: skillName ? `${skillName} pool` : 'Preferred pool',
  },
  { value: 'favourites', label: 'Favorites' },
  { value: 'browse', label: 'Browse all workers' },
];

export type StaffingTabsV2Props = {
  tab: StaffingTabV2Value;
  onTab: (tab: StaffingTabV2Value) => void;
  // Names the pool tab after the job's skill ("Bartender pool"), per the
  // prototype (ui-prototype/preferred-worker-pool #tabBtnPool). Optional so
  // a missing skill degrades to a generic label instead of " pool".
  skillName?: string;
};

const Wrapper = styled('div', {
  width: '100%',
});

const TabList = styled('div', {
  display: 'flex',
  borderBottom: '1px solid $colors$neutralLightest',
});

const TabButton = styled('button', {
  appearance: 'none',
  background: 'none',
  border: 'none',
  borderBottom: '2px solid transparent',
  cursor: 'pointer',
  padding: '10px 16px',
  fontSize: '14px',
  fontWeight: 600,
  color: '$colors$neutralLight',
  variants: {
    active: {
      true: {
        color: '$colors$themeDefault',
        borderBottomColor: '$colors$themeDefault',
      },
    },
  },
});

const StaffingTabsV2 = ({ tab, onTab, skillName }: StaffingTabsV2Props) => (
  <Wrapper>
    <TabList role="tablist">
      {topTabs(skillName).map((t) => (
        <TabButton
          key={t.value}
          active={tab === t.value}
          aria-selected={tab === t.value}
          role="tab"
          type="button"
          onClick={() => onTab(t.value)}
        >
          {t.label}
        </TabButton>
      ))}
    </TabList>
  </Wrapper>
);

export default StaffingTabsV2;
